import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { Clock, Sparkle, Wind } from "lucide-react";
import { toast } from "sonner";
import InstantOverlay from "@/components/julie/InstantOverlay";
import { getProfile } from "@/lib/profile";
import { FEELINGS, feelingLabel, feelingIcon } from "@/lib/feelings";
import { freePeriodHome } from "@/lib/freemium";
import { cutOpeningSound } from "@/lib/openingSound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [5, 10, 20, 30];
// Besoins génériques si un ressenti n'a pas encore sa liste en admin (4 max + « surprenez-moi »)
const DEFAULT_NEEDS = ["Me reposer / préparer mon sommeil", "Retrouver un peu d'énergie", "Relâcher mon corps", "Calmer mon mental"];
const SURPRISE = "Je ne sais pas, surprenez-moi";
const EASE = [0.22, 1, 0.36, 1];

const heroReveal = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1, delay: 0.15 * i, ease: EASE },
  }),
};

const sectionReveal = {
  // Apparition au chargement (pas au défilement) : fiable sur tous les téléphones
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE },
};

export default function Home() {
  const navigate = useNavigate();
  // Sélection initiale lue directement au montage (choix faits sur la page de première visite)
  const [need, setNeed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rav-instant") || "null")?.need ?? null;
    } catch {
      return null;
    }
  });
  const [minutes, setMinutes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rav-instant") || "null")?.minutes ?? null;
    } catch {
      return null;
    }
  });
  // Étape 2 : le besoin du moment, précisé après le ressenti (la cuisine des piliers reste cachée)
  const [besoin, setBesoin] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rav-instant") || "null")?.besoin ?? null;
    } catch {
      return null;
    }
  });
  const [needsByFeeling, setNeedsByFeeling] = useState({});
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const profile = getProfile();
  const isLogged = !!localStorage.getItem("rav-token");

  useEffect(() => {
    cutOpeningSound(); // règle absolue : la vague s'arrête dès l'arrivée sur l'accueil
    if (!isLogged) return;
    const token = localStorage.getItem("rav-token");
    axios
      .get(`${API}/credits`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setCredits(data))
      .catch(() => {});
  }, [isLogged]);

  // Règle de Julie : le clapotis n'existe qu'au tout début (pages d'accueil
  // d'entrée) — jamais sur l'accueil connecté, jamais pendant une séance.
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, 105]);
  const heroOpacity = useTransform(scrollY, [0, 230], [1, 0]);
  const miniOpacity = useTransform(scrollY, [190, 290], [0, 1]);
  const miniScale = useTransform(scrollY, [190, 290], [0.8, 1]);

  useEffect(() => {
    localStorage.setItem("rav-instant", JSON.stringify({ need, minutes, besoin }));
  }, [need, minutes, besoin]);

  // Besoins proposés par ressenti (éditables en admin, module Ressentis & besoins)
  useEffect(() => {
    axios
      .get(`${API}/feelings/public`)
      .then(({ data }) => {
        const map = {};
        (data.feelings || []).forEach((f) => {
          map[f.name] = f.needs || [];
        });
        setNeedsByFeeling(map);
      })
      .catch(() => {});
  }, []);

  const createInstant = () => {
    if (!need) {
      toast("Choisissez d’abord ce qui vous ferait du bien.");
      return;
    }
    if (!minutes) {
      toast("Indiquez le temps dont vous disposez.");
      return;
    }
    // Non connectée : la sélection est enregistrée et la page de création de compte s'ouvre
    if (!isLogged) {
      navigate("/connexion?mode=register");
      return;
    }
    setOverlayOpen(true);
  };

  return (
    <>
      <motion.header className="rav-hero" style={{ y: heroY, opacity: heroOpacity }}>
        <span className="rav-branch rav-branch-left" aria-hidden="true" />
        <span className="rav-branch rav-branch-right" aria-hidden="true" />
        <motion.div
          className="rav-emblem-wrap"
          data-testid="brand-emblem"
          variants={heroReveal}
          initial="hidden"
          animate="show"
          custom={0}
        >
          <div className="rav-halo" />
          <img
            src="/logo-accueil.webp"
            alt="Respire Aime Vibre — Julie Pomares Durand, Sophrologue"
            className="rav-logo-img rav-logo-img--new"
            draggable="false"
          />
        </motion.div>
        <motion.h1
          className="rav-tagline"
          data-testid="tagline-title"
          variants={heroReveal}
          initial="hidden"
          animate="show"
          custom={1}
        >
          L’Instant qui vous ressemble
        </motion.h1>
        <motion.div
          className="rav-divider"
          variants={heroReveal}
          initial="hidden"
          animate="show"
          custom={2}
        >
          <img src="/divider-heart.webp" alt="" aria-hidden="true" className="rav-divider-img" />
        </motion.div>
      </motion.header>

      <main className="rav-content">
        {profile?.prenom && (
          <motion.div {...sectionReveal} style={{ textAlign: "center" }}>
            <p className="rav-greeting" data-testid="home-greeting">
              Bonjour {profile.prenom}
            </p>
            {credits && !credits.unlimited && (
              <p className="rav-home-credits" data-testid="home-credits">
                {credits.trial
                  ? credits.remaining > 0
                    ? `Semaine découverte : ${credits.remaining} Instant${credits.remaining > 1 ? "s" : ""} à créer librement${credits.trial_end ? `, jusqu'au ${credits.trial_end}` : ""}${credits.card_free ? " — sans carte bancaire" : ""}`
                    : credits.card_free
                      ? "Vos 7 Instants offerts sont savourés 🤎 — choisissez votre formule pour continuer, sans engagement"
                      : `Vos 7 Instants d'essai sont savourés 🤎 — votre abonnement prend le relais ${credits.trial_end ? `le ${credits.trial_end}` : "à la fin de l'essai"}`
                  : credits.remaining > 0
                    ? `Il vous reste ${credits.remaining} Instant${credits.remaining > 1 ? "s" : ""} offert${credits.remaining > 1 ? "s" : ""} ${freePeriodHome(credits.period)}`
                    : `Vos Instants offerts sont savourés — retrouvez nos offres dans votre Profil`}
                {credits.trial && credits.card_free && (
                  <>
                    {" "}
                    <a href="/offres" data-testid="home-trial-offer-link" style={{ color: "inherit", textDecoration: "underline" }}>
                      Découvrir les formules
                    </a>
                  </>
                )}
              </p>
            )}
          </motion.div>
        )}
        <motion.section className="rav-step" data-testid="step-need" {...sectionReveal}>
          <div className="rav-step-head">
            <span className="rav-step-num">1</span>
            <h2 className="rav-step-title rav-step-title--upright">Comment vous sentez-vous aujourd’hui ?</h2>
          </div>
          <div className="rav-feel-grid">
            {FEELINGS.map((feeling) => (
              <button
                key={feeling.f}
                type="button"
                data-testid={`feeling-card-${feeling.f}`}
                className={`rav-feel-card${need === feeling.f ? " is-selected" : ""}${feeling.f === "Je ne sais pas vraiment" ? " rav-feel-card--wide" : ""}`}
                aria-pressed={need === feeling.f}
                onClick={() => {
                  setNeed(feeling.f);
                  setBesoin(null);
                }}
              >
                <img
                  src={feelingIcon(feeling, profile?.gender)}
                  alt=""
                  aria-hidden="true"
                  className="rav-feel-icon"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    if (e.currentTarget.src !== window.location.origin + feeling.icon) e.currentTarget.src = feeling.icon;
                  }}
                />
                <span>{feelingLabel(feeling, profile?.gender)}</span>
              </button>
            ))}
          </div>
        </motion.section>

        {need && need !== "Je ne sais pas vraiment" && (
          <motion.section
            className="rav-step"
            data-testid="step-besoin"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="rav-step-head">
              <span className="rav-step-num">2</span>
              <h2 className="rav-step-title">De quoi avez-vous besoin maintenant ?</h2>
            </div>
            <div className="rav-pills rav-needs-pills">
              {(needsByFeeling[need]?.length ? needsByFeeling[need] : DEFAULT_NEEDS).slice(0, 4).map((n, i) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`besoin-chip-${i}`}
                  className={`rav-pill rav-need-pill${besoin === n ? " is-selected" : ""}`}
                  aria-pressed={besoin === n}
                  onClick={() => setBesoin(n)}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                data-testid="besoin-surprise"
                className={`rav-pill rav-need-pill rav-need-surprise${besoin === SURPRISE ? " is-selected" : ""}`}
                aria-pressed={besoin === SURPRISE}
                onClick={() => setBesoin(SURPRISE)}
              >
                <Sparkle size={16} strokeWidth={1.6} />
                {SURPRISE}
              </button>
            </div>
          </motion.section>
        )}

        <motion.section
          className="rav-step"
          data-testid="step-duration"
          {...sectionReveal}
          transition={{ ...sectionReveal.transition, delay: 0.08 }}
        >
          <div className="rav-step-head">
            <span className="rav-step-num">{need && need !== "Je ne sais pas vraiment" ? "3" : "2"}</span>
            <h2 className="rav-step-title">Combien de temps avez-vous ?</h2>
          </div>
          <div className="rav-pills">
            {DURATIONS.map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`duration-pill-${m}`}
                className={`rav-pill${minutes === m ? " is-selected" : ""}`}
                aria-pressed={minutes === m}
                onClick={() => setMinutes(m)}
              >
                <Clock size={19} strokeWidth={1.6} />
                {m} min
              </button>
            ))}
          </div>
        </motion.section>

        <motion.div {...sectionReveal} transition={{ ...sectionReveal.transition, delay: 0.12 }} className="rav-home-actions">
          <motion.button
            type="button"
            whileTap={{ scale: 0.98 }}
            className="rav-primary-button"
            data-testid="create-instant-button"
            onClick={createInstant}
          >
            <Sparkle size={16} strokeWidth={1.6} />
            Créer mon <span className="rav-instant-em">INSTANT</span>
          </motion.button>

          {/* Ordre demandé par Julie : 1. rendez-vous avec Julie, 2. SOS */}
          <button
            type="button"
            className="rav-support"
            data-testid="rdv-card"
            onClick={() => navigate(isLogged ? "/reservation" : "/connexion")}
          >
            <span className="rav-support-icon">
              <img src="/icon-visio.webp" alt="" aria-hidden="true" className="rav-support-icon-img" />
            </span>
            <span>
              <span className="rav-support-title">Prendre rendez-vous avec Julie</span>
              <span className="rav-support-sub">En visio ou au cabinet, à votre rythme</span>
            </span>
          </button>

          {/* SOS et Visio/contact : visibles par toutes, mais l'inscription est OBLIGATOIRE pour y accéder */}
          <button
            type="button"
            className="rav-sos-button"
            data-testid="sos-link"
            onClick={() => navigate(isLogged ? "/sos" : "/connexion")}
          >
            <img src="/icon-sos.webp" alt="" aria-hidden="true" className="rav-sos-icon" />
            SOS — 5 minutes
          </button>

          <button
            type="button"
            className="rav-text-link rav-support-textlink"
            data-testid="support-card"
            onClick={() => navigate(isLogged ? "/echanger" : "/connexion")}
          >
            Une question ? Échanger avec Julie
          </button>
          {isLogged && (
            <button
              type="button"
              className="rav-text-link rav-explorer-link"
              data-testid="explorer-link"
              onClick={() => navigate("/explorer")}
            >
              Ou choisir librement À la carte
            </button>
          )}
        </motion.div>

        <motion.div
          className="rav-quote-wrap"
          {...sectionReveal}
          transition={{ ...sectionReveal.transition, delay: 0.1 }}
        >
          <img src="/ornament.webp" alt="" aria-hidden="true" className="rav-ornament" />
          <p className="rav-quote" data-testid="home-quote">
            Aujourd’hui, prenez un moment pour vous.
          </p>
        </motion.div>
      </main>

      <motion.div
        className="rav-mini-logo"
        data-testid="mini-logo"
        style={{ opacity: miniOpacity, scale: miniScale }}
      >
        <img src="/logo-accueil.webp" alt="Respire Aime Vibre" />
      </motion.div>
      <InstantOverlay
        open={overlayOpen}
        needLabel={need ?? ""}
        minutes={minutes ?? 0}
        onClose={() => setOverlayOpen(false)}
        onContinue={() => {
          setOverlayOpen(false);
          navigate("/instant");
        }}
      />
    </>
  );
}
